import { useSelector } from 'react-redux';
import { H1, LabelBold, LabelRegular } from '@comeon/ui';
import { selectLocale } from '~/components/account/ducks/index.js';
import { sidebarTop } from '~/utils/layout-utils';
import { LABEL_MAPPING } from '../constants/we-spin-boost-constants.js';
import EventTypeTitle from '../components/event-type-title.js';
import styled from 'styled-components';
import useTranslations from '~/components/translation/use-translations';
import { WE_SPIN_BUY_IN_TEXT_KEYS } from '../constants/we-spin-text-keys';
import Loading from '~/components/misc/loading';
import {
	WE_SPIN_DASHBOARD_EVENT_DETAIL_VIEW_TEXT_KEYS,
	WE_SPIN_DASHBOARD_STREAM_FIELD_TEXT_KEYS
} from '../constants/we-spin-dashboard-text-keys';
import currencyFormatter from '~/libs/currency-formatter';
import { useMemo } from 'react';
import ParticipantsText from '../components/participants-text';
import Icon, { ICON_LIVE_STREAM_LINE, ICON_USER } from '~/components/icon';
import { RUNNING } from '../constants/we-spin-stream-states';
import { WE_SPIN_EVENT_TAG_ALL_TEXT_KEYS } from '~/components/we-spin/new-flow/constants/we-spin-event-tags';
import BoostBadge from '~/components/we-spin/new-flow/components/boost-badge';

// Large screen height must be set to calc(100vh - 64px);
// We also need to calculate height if header or regulations header etc
// Use layout-utils.js -> sidebarTop()
// Get props into stream-element.
// Do we have some kind of uiContext to see if we have regulationHeader etc...

const CONFIG = {
	small: {
		height: '214px',
		width: '375px'
	},
	large: {
		height: '214px',
		width: undefined
	},
	lobby: {
		height: '198px',
		width: undefined
	}
};

const getIframeSize = (hasHeader, hasProductNav, hasStaticHeader) => {
	const offset = sidebarTop({ hasHeader, hasProductNav, hasStaticHeader });
	return {
		small: {
			height: '214px',
			width: '375px'
		},
		large: {
			style: { height: `calc(100vh - ${offset})` },
			width: undefined
		},
		lobby: {
			height: '198px',
			width: '350px'
		}
	};
};

// const SIZES = '480px';

const ImageWrapper = styled.div`
	position: relative;

	img {
		max-height: 214px;
		object-position: center;
	}

	& > .event-type-title-bg {
		height: 198px;

		& > .event-type-title-text {
			font-size: 36px;
			word-spacing: 30px;
		}
	}
`;

const ParticipantsWrapper = styled.div`
	position: absolute;
	bottom: 12px;
	left: 12px;
	span {
		padding-right: 6px;
		padding-left: 6px;
	}
`;

const BoostWrapper = styled.div`
	position: absolute;
	bottom: 12px;
	right: 12px;
	span {
		padding-right: 6px;
		padding-left: 6px;
	}
`;

const InfoWrapper = styled.div`
	display: flex;
	border-bottom: 1px solid rgba(var(--font-supportive), 0.2);
	justify-content: space-between;

	& > div > small {
		margin-bottom: 4px;
	}

	& > div:not(:first-child) {
		border-left: 1px solid rgba(var(--font-supportive), 0.2);
		margin-right: ${({ isSmall }) => (isSmall ? '36px' : '0')};
	}
`;

const Flag = styled.div`
	height: 16px;
	width: 16px;
	border-radius: 50px;
`;

const LiveStreamIcon = styled(Icon)`
	padding: 0 !important;
`;

const StreamDescription = styled(LabelRegular)`
	color: rgba(var(--font-supportive), 1);
`;
// TODO: Check why srcSet isn't working
const getStreamUrl = urlAsString => {
	try {
		let url = new URL(urlAsString);
		if (urlAsString.includes('www.twitch.tv')) {
			// Convert twitch channel urls to the embed player url
			const channel = url.pathname.substring(1);
			url = new URL(`https://player.twitch.tv/?channel=${channel}${url.search}`);
		}

		if (urlAsString.includes('twitch')) {
			url.searchParams.set('parent', window.location.hostname);
		}

		return url.toString();
	} catch (err) {
		return urlAsString;
	}
};

const TEXT_KEYS = [
	...WE_SPIN_DASHBOARD_STREAM_FIELD_TEXT_KEYS,
	...WE_SPIN_DASHBOARD_EVENT_DETAIL_VIEW_TEXT_KEYS,
	...WE_SPIN_BUY_IN_TEXT_KEYS,
	...WE_SPIN_EVENT_TAG_ALL_TEXT_KEYS
];

const StreamElement = ({
	type,
	streamLinkUrl,
	size = 'small',
	header,
	productnav,
	regulations,
	interactive = true,
	// srcSet = '',
	streamView,
	showEventTagBg,
	compact = false,
	hideText = false,
	card = false
}) => {
	const { translations: tr, isLoadingTranslations } = useTranslations(TEXT_KEYS);
	const locale = useSelector(selectLocale);
	const isPrivateStreamer = streamView?.privateStreamer;

	const boostCaption = `${
		streamView?.boost
			? `${currencyFormatter.format(streamView?.boost.amount, streamView?.boost.currency, locale, true)} ${
					LABEL_MAPPING[streamView?.boost.type]
				}`
			: ``
	}`;

	const startDate = useMemo(
		() =>
			`${new Date(streamView?.start).toLocaleString([], {
				day: '2-digit',
				month: 'short'
			})} - ${new Date(streamView?.start).toLocaleString([], {
				hour: '2-digit',
				minute: '2-digit'
			})}`,
		[streamView?.start]
	);

	if (isLoadingTranslations) {
		return <Loading />;
	}

	return type === 'STREAM' && streamLinkUrl ? (
		<div>
			<iframe
				title="stream video"
				className={`${interactive ? '' : 'u-pointer-events-none'}`}
				src={getStreamUrl(streamLinkUrl)}
				frameBorder="0"
				scrolling="no"
				allowFullScreen
				allow="autoplay"
				{...getIframeSize(header, productnav, regulations)[size]}
			/>
			{!card && (
				<div className="u-mb1 u-pt1">
					<H1 className="u-flex u-align-vert">
						{streamView?.name}{' '}
						{streamView?.state === RUNNING && (
							<LiveStreamIcon className="u-ml1" type={ICON_LIVE_STREAM_LINE} />
						)}
					</H1>
					<StreamDescription my={1} className="wespin-streamer-description u-multi-line-truncate">
						{streamView?.description}
					</StreamDescription>
				</div>
			)}
		</div>
	) : (
		<div>
			<ImageWrapper>
				{!showEventTagBg ? (
					<img
						className="u-object-fit-cover"
						src={streamView.streamBgUrl}
						alt=""
						style={{ ...CONFIG[size] }}
					/>
				) : (
					<EventTypeTitle eventType={streamView?.eventTags[0]} translations={tr} transparentBg />
				)}
				{!card && !compact && (
					<ParticipantsWrapper>
						<ParticipantsText>
							<Icon type={ICON_USER} />
							{streamView?.noParticipants}
						</ParticipantsText>
					</ParticipantsWrapper>
				)}
				<BoostWrapper>
					<BoostBadge>{boostCaption}</BoostBadge>
				</BoostWrapper>
			</ImageWrapper>
			{!card && (
				<div className={compact ? 'u-mt1' : ''}>
					{!compact && (
						<InfoWrapper className="u-flex" isSmall={isPrivateStreamer}>
							<div className="u-p1">
								<small>{tr?.WE_SPIN_DASHBOARD_STREAM_FIELD_GROUP_WHEN}</small>
								<LabelBold>{startDate}</LabelBold>
							</div>
							<div className="u-p1">
								<small>{tr?.WE_SPIN_DASHBOARD_LIVE_START_EVENT_BALANCE}</small>
								<LabelBold>
									{currencyFormatter.format(
										streamView?.balance,
										streamView?.currencyCode,
										locale,
										true
									)}
								</LabelBold>
							</div>
							{!isPrivateStreamer && (
								<div className="u-p1 u-pr2">
									<small>{tr?.WE_SPIN_BUY_IN_LANGUAGE_LABEL}</small>
									<Flag
										className={`flag flag--${
											streamView?.language === 'en' ? 'gb' : streamView?.language
										}`}
									/>
								</div>
							)}
						</InfoWrapper>
					)}
					{!hideText && (
						<div className="u-mb1 u-pt1">
							<H1 className="u-flex u-align-vert">
								{streamView?.name}{' '}
								{streamView?.state === RUNNING && (
									<LiveStreamIcon className="u-ml1" type={ICON_LIVE_STREAM_LINE} />
								)}
							</H1>
							<StreamDescription my={1} className="wespin-streamer-description u-multi-line-truncate">
								{streamView?.description}
							</StreamDescription>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default StreamElement;
